import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaArrowRight, FaLaptopCode, FaCloud, FaCogs, FaShieldAlt, FaRocket } from 'react-icons/fa';
import { theme } from '../../styles/theme';

// Page Header
const PageHeader = styled.div`
  background: linear-gradient(to right, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), 
              url('/images/solutions-bg.jpg') no-repeat center center;
  background-size: cover;
  height: 40vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 24px;
  margin-top: 80px;
  
  @media screen and (max-width: 480px) {
    height: 30vh;
  }
`;

const PageTitle = styled.h1`
  color: ${theme.colors.secondary};
  font-size: 3rem;
  text-align: center;
  max-width: 1200px;
  width: 100%;
  
  @media screen and (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

// Section Container
const SectionContainer = styled.div`
  padding: 100px 0;
  background: ${theme.colors.primary};
  border-top: ${({ dark }) => (dark ? `1px solid ${theme.colors.secondary}` : 'none')};
  border-bottom: ${({ dark }) => (dark ? `1px solid ${theme.colors.secondary}` : 'none')};
  
  @media screen and (max-width: 768px) {
    padding: 70px 0;
  }
  
  @media screen and (max-width: 480px) {
    padding: 50px 0;
  }
`;

const SectionWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  
  @media screen and (max-width: 480px) {
    padding: 0 16px;
  }
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 24px;
  text-align: center;
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const SectionSubheading = styled.p`
  font-size: 1.2rem;
  color: ${theme.colors.text.primary};
  margin-bottom: 64px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

// Solution Card
const SolutionCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  margin-bottom: 100px;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
    margin-bottom: 70px;
  }
  
  @media screen and (max-width: 480px) {
    margin-bottom: 50px;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SolutionContent = styled.div`
  display: flex;
  flex-direction: ${({ imgStart }) => (imgStart ? 'row-reverse' : 'row')};
  align-items: center;
  
  @media screen and (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

const Column1 = styled.div`
  flex: 1;
  padding: 0 15px;
  
  @media screen and (max-width: 768px) {
    margin-top: 30px;
    padding: 0 10px;
  }
  
  @media screen and (max-width: 480px) {
    margin-top: 20px;
    padding: 0 5px;
  }
`;

const Column2 = styled.div`
  flex: 1;
  padding: 0 15px;
  
  @media screen and (max-width: 768px) {
    margin-bottom: 0;
    padding: 0 10px;
  }
  
  @media screen and (max-width: 480px) {
    padding: 0 5px;
  }
`;

const SolutionImage = styled.div`
  width: 100%;
  height: 400px;
  background: ${theme.colors.accent};
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid ${theme.colors.secondary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media screen and (max-width: 768px) {
    height: 350px;
  }
  
  @media screen and (max-width: 480px) {
    height: 250px;
  }
`;

const SolutionTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 16px;
  color: ${theme.colors.secondary};
  
  @media screen and (max-width: 768px) {
    font-size: 1.8rem;
  }
  
  @media screen and (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: 12px;
  }
`;

const SolutionDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 24px;
  color: ${theme.colors.text.primary};
  
  @media screen and (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.5;
    margin-bottom: 16px;
  }
`;

// Features List
const FeaturesList = styled.div`
  margin-top: 32px;
  
  @media screen and (max-width: 480px) {
    margin-top: 20px;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const FeatureIcon = styled.div`
  color: ${theme.colors.secondary};
  font-size: 1.2rem;
  margin-right: 10px;
  margin-top: 3px;
`;

const FeatureText = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: ${theme.colors.text.primary};
`;

// Learn More Button
const LearnMoreBtn = styled.a`
  display: inline-flex;
  align-items: center;
  background: transparent;
  color: ${theme.colors.secondary};
  font-size: 1.1rem;
  text-decoration: none;
  font-weight: 600;
  margin-top: 24px;
  
  &:hover {
    color: ${theme.colors.tertiary};
    transition: 0.3s ease-out;
  }
`;

const ArrowIcon = styled(FaArrowRight)`
  margin-left: 8px;
  font-size: 0.9rem;
`;

// Logo Components
const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent});
`;

const LogoIcon = styled.div`
  position: relative;
  font-size: 5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 1.5rem;
  
  @media screen and (max-width: 768px) {
    font-size: 4rem;
  }
  
  @media screen and (max-width: 480px) {
    font-size: 3.5rem;
  }
`;

const LogoText = styled.h3`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${theme.colors.secondary};
  margin-bottom: 0.5rem;
  text-align: center;
  
  @media screen and (max-width: 768px) {
    font-size: 2.2rem;
  }
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const LogoTagline = styled.p`
  font-size: 1.2rem;
  color: ${theme.colors.text.primary};
  text-align: center;
  
  @media screen and (max-width: 480px) {
    font-size: 1rem;
  }
`;

const Solutions = () => {
  return (
    <>
      <PageHeader>
        <PageTitle>Our Services</PageTitle>
      </PageHeader>
      
      <SectionContainer>
        <SectionWrapper>
          <SectionHeading>Service Capabilities</SectionHeading>
          <SectionSubheading>
            We build modern technology solutions tailored to your business needs. From web development to AI integration, we help African businesses transform and grow.
          </SectionSubheading>
          
          <SolutionCard
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <SolutionContent imgStart={false}>
              <Column1>
                <SolutionTitle>Web & Mobile Development</SolutionTitle>
                <SolutionDescription>
                  We build modern websites, business systems, customer portals, and mobile applications tailored to your business needs.
                </SolutionDescription>
                <FeaturesList>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Custom website development</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Mobile app development (iOS & Android)</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Customer portals and dashboards</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>E-commerce solutions</FeatureText>
                  </FeatureItem>
                </FeaturesList>
                <LearnMoreBtn href="#contact">
                  Learn More <ArrowIcon />
                </LearnMoreBtn>
              </Column1>
              <Column2>
                <SolutionImage>
                  <LogoContainer>
                    <LogoIcon>
                      <FaLaptopCode />
                    </LogoIcon>
                    <LogoText>Web & Mobile</LogoText>
                    <LogoTagline>Development</LogoTagline>
                  </LogoContainer>
                </SolutionImage>
              </Column2>
            </SolutionContent>
          </SolutionCard>
          
          <SolutionCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.7, 
              delay: 0.4,
              ease: [0.6, 0.05, 0.01, 0.99] 
            }}
          >
            <SolutionContent imgStart={true}>
              <Column1>
                <SolutionTitle>Artificial Intelligence</SolutionTitle>
                <SolutionDescription>
                  AI assistants, workflow automation, chatbots, document processing, and intelligent business solutions to streamline your operations.
                </SolutionDescription>
                <FeaturesList>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>AI-powered chatbots and assistants</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Workflow automation</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Document processing and analysis</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Predictive analytics</FeatureText>
                  </FeatureItem>
                </FeaturesList>
                <LearnMoreBtn href="#contact">
                  Learn More <ArrowIcon />
                </LearnMoreBtn>
              </Column1>
              <Column2>
                <SolutionImage>
                  <LogoContainer style={{ background: 'linear-gradient(135deg, #e3f2fd, #2196f3)' }}>
                    <LogoIcon style={{ color: '#fff' }}>
                      <FaCogs />
                    </LogoIcon>
                    <LogoText style={{ color: '#fff' }}>Artificial</LogoText>
                    <LogoTagline style={{ color: '#fff9' }}>Intelligence</LogoTagline>
                  </LogoContainer>
                </SolutionImage>
              </Column2>
            </SolutionContent>
          </SolutionCard>
          
          <SolutionCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.7, 
              delay: 0.6,
              ease: [0.6, 0.05, 0.01, 0.99] 
            }}
          >
            <SolutionContent imgStart={false}>
              <Column1>
                <SolutionTitle>Cloud Solutions</SolutionTitle>
                <SolutionDescription>
                  Microsoft Azure deployment, cloud migration, hosting, monitoring, backups, and infrastructure management for scalable business operations.
                </SolutionDescription>
                <FeaturesList>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Cloud migration and deployment</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Infrastructure management</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Monitoring and backups</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Scalable hosting solutions</FeatureText>
                  </FeatureItem>
                </FeaturesList>
                <LearnMoreBtn href="#contact">
                  Learn More <ArrowIcon />
                </LearnMoreBtn>
              </Column1>
              <Column2>
                <SolutionImage>
                  <LogoContainer style={{ background: 'linear-gradient(135deg, #e8f5e9, #4caf50)' }}>
                    <LogoIcon style={{ color: '#fff' }}>
                      <FaCloud />
                    </LogoIcon>
                    <LogoText style={{ color: '#fff' }}>Cloud</LogoText>
                    <LogoTagline style={{ color: '#fff9' }}>Solutions</LogoTagline>
                  </LogoContainer>
                </SolutionImage>
              </Column2>
            </SolutionContent>
          </SolutionCard>

          <SolutionCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.7, 
              delay: 0.8,
              ease: [0.6, 0.05, 0.01, 0.99] 
            }}
          >
            <SolutionContent imgStart={true}>
              <Column1>
                <SolutionTitle>Business Software</SolutionTitle>
                <SolutionDescription>
                  CRM systems, booking platforms, inventory management, HR systems, and custom enterprise applications for your business.
                </SolutionDescription>
                <FeaturesList>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>CRM and customer management</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Booking and scheduling platforms</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Inventory management systems</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Custom enterprise applications</FeatureText>
                  </FeatureItem>
                </FeaturesList>
                <LearnMoreBtn href="#contact">
                  Learn More <ArrowIcon />
                </LearnMoreBtn>
              </Column1>
              <Column2>
                <SolutionImage>
                  <LogoContainer style={{ background: 'linear-gradient(135deg, #fff3e0, #ff9800)' }}>
                    <LogoIcon style={{ color: '#fff' }}>
                      <FaRocket />
                    </LogoIcon>
                    <LogoText style={{ color: '#fff' }}>Business</LogoText>
                    <LogoTagline style={{ color: '#fff9' }}>Software</LogoTagline>
                  </LogoContainer>
                </SolutionImage>
              </Column2>
            </SolutionContent>
          </SolutionCard>

          <SolutionCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.7, 
              delay: 1.0,
              ease: [0.6, 0.05, 0.01, 0.99] 
            }}
          >
            <SolutionContent imgStart={false}>
              <Column1>
                <SolutionTitle>Cybersecurity & IT</SolutionTitle>
                <SolutionDescription>
                  Security assessments, networking, CCTV integration, IT support, and managed services to protect your business.
                </SolutionDescription>
                <FeaturesList>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Security assessments</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Network setup and management</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>CCTV integration</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>IT support and managed services</FeatureText>
                  </FeatureItem>
                </FeaturesList>
                <LearnMoreBtn href="#contact">
                  Learn More <ArrowIcon />
                </LearnMoreBtn>
              </Column1>
              <Column2>
                <SolutionImage>
                  <LogoContainer style={{ background: 'linear-gradient(135deg, #ffebee, #f44336)' }}>
                    <LogoIcon style={{ color: '#fff' }}>
                      <FaShieldAlt />
                    </LogoIcon>
                    <LogoText style={{ color: '#fff' }}>Cybersecurity</LogoText>
                    <LogoTagline style={{ color: '#fff9' }}>& IT</LogoTagline>
                  </LogoContainer>
                </SolutionImage>
              </Column2>
            </SolutionContent>
          </SolutionCard>

          <SolutionCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.7, 
              delay: 1.2,
              ease: [0.6, 0.05, 0.01, 0.99] 
            }}
          >
            <SolutionContent imgStart={true}>
              <Column1>
                <SolutionTitle>Digital Transformation</SolutionTitle>
                <SolutionDescription>
                  Helping businesses modernize operations through software, automation, and cloud technologies for competitive advantage.
                </SolutionDescription>
                <FeaturesList>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Business process automation</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Digital strategy consulting</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Legacy system modernization</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Technology adoption planning</FeatureText>
                  </FeatureItem>
                </FeaturesList>
                <LearnMoreBtn href="#contact">
                  Learn More <ArrowIcon />
                </LearnMoreBtn>
              </Column1>
              <Column2>
                <SolutionImage>
                  <LogoContainer style={{ background: 'linear-gradient(135deg, #f3e5f5, #9c27b0)' }}>
                    <LogoIcon style={{ color: '#fff' }}>
                      <FaRocket />
                    </LogoIcon>
                    <LogoText style={{ color: '#fff' }}>Digital</LogoText>
                    <LogoTagline style={{ color: '#fff9' }}>Transformation</LogoTagline>
                  </LogoContainer>
                </SolutionImage>
              </Column2>
            </SolutionContent>
          </SolutionCard>
        </SectionWrapper>
      </SectionContainer>
    </>
  );
};

export default Solutions;
